import { useState } from 'react';
import { motion } from 'framer-motion';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import { SEOHead } from '../../components/tools/SEOHead';
import { ScoreDisclaimer } from '../../components/tools/ScoreDisclaimer';
import { ACTFAQ } from '../../components/tools/ACTFAQ';
import Footer from '../../components/Footer';
import 'react-circular-progressbar/dist/styles.css';
import { Helmet } from 'react-helmet'; // Add this import if not already using Helmet

export function ACTCalculator() {
  const [sections, setSections] = useState([
    { name: 'English', score: 0, maxScore: 75 },
    { name: 'Math', score: 0, maxScore: 60 },
    { name: 'Reading', score: 0, maxScore: 40 },
    { name: 'Science', score: 0, maxScore: 40 }
  ]);

  const calculateScaledScore = (raw, max) => {
    return Math.round((raw / max) * 36);
  };

  const compositeScore = Math.round(
    sections.reduce((sum, section) => 
      sum + calculateScaledScore(section.score, section.maxScore), 0) / sections.length
  );

  const handleScoreChange = (index, value) => {
    const newSections = [...sections];
    newSections[index].score = Math.min(newSections[index].maxScore, Math.max(0, value));
    setSections(newSections);
  };

  // Get a text description of the ACT score
  const getScoreDescription = (score) => {
    if (score >= 33) return "Exceptional (Top 1%)";
    if (score >= 29) return "Excellent (Top 10%)";
    if (score >= 24) return "Above Average (Top 25%)";
    if (score >= 20) return "Average (Top 50%)";
    if (score >= 16) return "Below Average";
    return "Needs Improvement";
  };

  // Get college admission prospects based on ACT score
  const getCollegeProspects = (score) => {
    if (score >= 33) return "Competitive for Ivy League and top-tier universities";
    if (score >= 29) return "Strong candidate for selective universities";
    if (score >= 24) return "Competitive for many public and private universities";
    if (score >= 20) return "Meets requirements for most state universities";
    return "Consider community college or test-optional schools";
  };

  return (
    <>
      <SEOHead
        title="ACT Score Calculator 2025 | Free ACT Composite Score Estimator"
        description="Calculate your ACT score instantly with our free calculator. Get accurate scoring for English, Math, Reading, and Science sections with our official 2025 ACT score conversion tool. See how your composite score compares to college requirements."
        keywords={[
          "ACT score calculator",
          "ACT calculator",
          "ACT composite score",
          "2025 ACT scoring",
          "ACT score conversion",
          "ACT score estimator",
          "Free ACT calculator",
          "ACT English score",
          "ACT Math score",
          "ACT Reading score",
          "ACT Science score",
          "College ACT requirements"
        ]}
        path="/tools/act-score-calculator"
      />

      {/* Structured data for rich results */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "ACT Score Calculator",
            "description": "Free tool to calculate your ACT composite score and section scores based on official scoring guidelines",
            "applicationCategory": "EducationalApplication",
            "operatingSystem": "Web",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            }
          })}
        </script>
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-bold text-gray-800 mb-2">ACT Score Calculator 2025</h1>
          <p className="text-gray-600 text-lg">Calculate your ACT composite score instantly with our free tool</p>
          <p className="text-gray-500 mt-2">Updated with official 2025 ACT scoring guidelines</p>
        </motion.div>

        <div className="bg-blue-50 p-5 rounded-lg mb-8 border border-blue-200">
          <h2 className="text-xl font-semibold text-blue-800 mb-2">About This ACT Score Calculator</h2>
          <p>This free ACT calculator converts your raw scores to scaled scores (1-36) for each section and calculates your composite score. Enter the number of questions you answered correctly in each section to get an instant estimate of your ACT score.</p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {sections.map((section, index) => (
            <motion.div
              key={section.name}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white p-6 rounded-lg shadow-md"
            >
              <h2 className="text-xl font-semibold mb-4">{section.name} Section</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-2">
                    Raw Score (Questions Correct: 0-{section.maxScore})
                  </label>
                  <div className="flex gap-4">
                    <input
                      type="range"
                      min="0"
                      max={section.maxScore}
                      value={section.score}
                      onChange={(e) => handleScoreChange(index, parseInt(e.target.value))}
                      className="flex-1"
                      aria-label={`${section.name} raw score slider`}
                    />
                    <input
                      type="number"
                      min="0"
                      max={section.maxScore}
                      value={section.score}
                      onChange={(e) => handleScoreChange(index, parseInt(e.target.value) || 0)}
                      className="w-20 px-3 py-1 border rounded-md text-center"
                      aria-label={`${section.name} raw score input`}
                    />
                  </div>
                </div>
                <div className="text-right text-sm text-gray-600">
                  Scaled Score: <span className="font-semibold">{calculateScaledScore(section.score, section.maxScore)}</span>/36
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-8 bg-gradient-to-r from-blue-600 to-indigo-600 p-8 rounded-lg text-white text-center"
        >
          <h2 className="text-2xl font-bold mb-4">Your ACT Composite Score</h2>
          <div className="flex flex-col md:flex-row items-center justify-center gap-8">
            <div className="w-40 h-40">
              <CircularProgressbar
                value={(compositeScore / 36) * 100}
                text={compositeScore.toString()}
                styles={buildStyles({
                  textSize: '32px',
                  pathColor: `rgba(255, 255, 255, ${compositeScore / 36})`,
                  textColor: '#ffffff',
                  trailColor: 'rgba(255, 255, 255, 0.2)',
                })}
              />
            </div>
            <div className="text-left">
              <p className="text-xl mb-2">Score Rating: <span className="font-bold">{getScoreDescription(compositeScore)}</span></p>
              <p className="text-lg">College Prospects: <span className="font-medium">{getCollegeProspects(compositeScore)}</span></p>
            </div>
          </div>
        </motion.div>

        <section className="mt-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">How to Use This ACT Score Calculator</h2>
          <ol className="list-decimal pl-6 space-y-2">
            <li>Enter the number of questions you answered correctly for each section</li>
            <li>The calculator automatically converts raw scores to the ACT's 1-36 scale</li>
            <li>Your composite score is the average of all four section scores, rounded to the nearest whole number</li>
            <li>Use this information to track your progress and set study goals</li>
          </ol>
        </section>

        <ScoreDisclaimer />

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-8 bg-white p-6 rounded-lg shadow-md"
        >
          <h2 className="text-xl font-semibold mb-4">ACT Score Guide</h2>
          <div className="grid gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold text-lg mb-2">Understanding Your ACT Score</h3>
              <p className="text-gray-600">
                The ACT Composite Score is the average of your four section scores (English, Math, Reading, and Science), 
                rounded to the nearest whole number. Scores range from 1-36, with 36 being the highest possible score.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { range: '33-36', level: 'Exceptional' },
                { range: '29-32', level: 'Excellent' },
                { range: '24-28', level: 'Above Average' },
                { range: '20-23', level: 'Average' }
              ].map((score) => (
                <div key={score.range} className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-xl font-bold text-blue-600">{score.range}</div>
                  <div className="text-sm text-gray-600">{score.level}</div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        <section className="my-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">ACT Score Ranges for Top Universities</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="py-2 px-4 border">University</th>
                  <th className="py-2 px-4 border">Average ACT Score</th>
                  <th className="py-2 px-4 border">25th-75th Percentile</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="py-2 px-4 border">Harvard University</td>
                  <td className="py-2 px-4 border">34</td>
                  <td className="py-2 px-4 border">33-35</td>
                </tr>
                <tr>
                  <td className="py-2 px-4 border">MIT</td>
                  <td className="py-2 px-4 border">35</td>
                  <td className="py-2 px-4 border">34-36</td>
                </tr>
                <tr>
                  <td className="py-2 px-4 border">University of Michigan</td>
                  <td className="py-2 px-4 border">32</td>
                  <td className="py-2 px-4 border">31-34</td>
                </tr>
                <tr>
                  <td className="py-2 px-4 border">Ohio State University</td>
                  <td className="py-2 px-4 border">28</td>
                  <td className="py-2 px-4 border">26-32</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="my-8 bg-gray-50 p-6 rounded-lg">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">ACT vs. SAT: Which Should You Take?</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-xl font-semibold mb-2">ACT Highlights</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Includes a Science section</li>
                <li>More straightforward questions</li>
                <li>Faster-paced test (less time per question)</li>
                <li>All multiple choice questions</li>
                <li>Optional writing section</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">SAT Highlights</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>More time per question</li>
                <li>No separate Science section</li>
                <li>Greater emphasis on vocabulary</li>
                <li>Digital adaptive format (as of 2023)</li>
                <li>No penalty for wrong answers</li>
              </ul>
            </div>
          </div>
          <p className="mt-4">Many students take both tests to see which one better showcases their strengths. Most colleges accept either test score for admissions.</p>
        </section>

        <ACTFAQ />

        <div className="bg-blue-50 p-6 rounded-lg my-8 border border-blue-200">
          <h2 className="text-xl font-semibold text-blue-800 mb-2">Need Help Improving Your ACT Score?</h2>
          <p className="mb-4">Use our free ACT score calculator to track your progress as you prepare for the test. For personalized study plans and expert guidance, explore our ACT prep resources.</p>
          <p>Remember that this calculator provides an estimate based on official scoring tables. Your actual ACT score may vary slightly.</p>
        </div>

        <section className="my-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">ACT Scoring Tips and Strategies</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-5 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-2">English Section Tips</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Focus on grammar rules and punctuation</li>
                  <li>Practice identifying redundancy in sentences</li>
                  <li>Learn common idioms and their correct usage</li>
                  <li>Review subject-verb agreement rules</li>
                </ul>
              </div>
              <div className="bg-white p-5 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-2">Math Section Tips</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Review algebra, geometry, and trigonometry concepts</li>
                  <li>Practice with a calculator you'll use on test day</li>
                  <li>Memorize common formulas not provided on the test</li>
                  <li>Work on time management for complex problems</li>
                </ul>
              </div>
              <div className="bg-white p-5 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-2">Reading Section Tips</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Practice active reading techniques</li>
                  <li>Focus on finding evidence in the passage</li>
                  <li>Read questions before the passage to guide your reading</li>
                  <li>Practice with various passage types (prose, humanities, social sciences)</li>
                </ul>
              </div>
              <div className="bg-white p-5 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-2">Science Section Tips</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Practice interpreting graphs, charts, and tables</li>
                  <li>Focus on data analysis rather than scientific knowledge</li>
                  <li>Learn to identify patterns and relationships in data</li>
                  <li>Practice comparing multiple viewpoints in conflicting studies</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="my-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">ACT Score Conversion Chart</h2>
            <p className="mb-4">This simplified chart shows approximately how raw scores convert to scaled scores. Actual conversions may vary slightly from test to test.</p>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="py-2 px-3 border">Scaled Score</th>
                    <th className="py-2 px-3 border">English Raw Score</th>
                    <th className="py-2 px-3 border">Math Raw Score</th>
                    <th className="py-2 px-3 border">Reading Raw Score</th>
                    <th className="py-2 px-3 border">Science Raw Score</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="py-2 px-3 border font-semibold">36</td>
                    <td className="py-2 px-3 border">74-75</td>
                    <td className="py-2 px-3 border">58-60</td>
                    <td className="py-2 px-3 border">40</td>
                    <td className="py-2 px-3 border">40</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 border font-semibold">30</td>
                    <td className="py-2 px-3 border">65-67</td>
                    <td className="py-2 px-3 border">48-50</td>
                    <td className="py-2 px-3 border">32-33</td>
                    <td className="py-2 px-3 border">32-33</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 border font-semibold">25</td>
                    <td className="py-2 px-3 border">55-58</td>
                    <td className="py-2 px-3 border">38-40</td>
                    <td className="py-2 px-3 border">26-27</td>
                    <td className="py-2 px-3 border">26-27</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 border font-semibold">20</td>
                    <td className="py-2 px-3 border">44-47</td>
                    <td className="py-2 px-3 border">28-30</td>
                    <td className="py-2 px-3 border">20-21</td>
                    <td className="py-2 px-3 border">20-21</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="my-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Frequently Asked Questions About ACT Scoring</h2>
            <div className="space-y-4">
              <div className="bg-white p-5 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-2">What is a good ACT score?</h3>
                <p>The average ACT score is around 20-21. Scores of 24+ are considered above average, while scores of 30+ put you in the top 10% of test takers. The definition of a "good" score depends on the colleges you're applying to.</p>
              </div>
              <div className="bg-white p-5 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-2">How is the ACT scored?</h3>
                <p>Each section (English, Math, Reading, Science) is scored on a scale of 1-36. Your composite score is the average of these four scores, rounded to the nearest whole number.</p>
              </div>
              <div className="bg-white p-5 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-2">Is there a penalty for wrong answers on the ACT?</h3>
                <p>No, there is no penalty for wrong answers on the ACT. Your raw score is simply the number of questions you answer correctly, so it's always better to guess than to leave a question blank.</p>
              </div>
            </div>
          </section>

          <div className="bg-gradient-to-r from-blue-100 to-indigo-100 p-6 rounded-lg my-8 border border-blue-200">
            <h2 className="text-xl font-semibold text-blue-800 mb-2">Why Use Our ACT Score Calculator?</h2>
            <ul className="list-check pl-6 space-y-2">
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>Updated with the latest 2025 ACT scoring guidelines</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>Accurate section and composite score calculations</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>Instant results with college admissions context</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>Free to use with no registration required</span>
              </li>
            </ul>
          </div>

          <Footer />
        </div>
      </>
    );
}
